import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  AccountBalance,
  Category,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface CostCodeFormData {
  projectId: string;
  code: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  unitOfMeasure: string;
  standardRate: number;
  estimatedQuantity: number;
  laborRate: number;
  materialRate: number;
  equipmentRate: number;
  overhead: number;
  profit: number;
  isActive: boolean;
  notes: string;
}

const CostCodeCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [formData, setFormData] = useState<CostCodeFormData>({
    projectId: '',
    code: '',
    name: '',
    description: '',
    category: 'labor',
    subcategory: '',
    unitOfMeasure: 'hour',
    standardRate: 0,
    estimatedQuantity: 0,
    laborRate: 0,
    materialRate: 0,
    equipmentRate: 0,
    overhead: 0,
    profit: 0,
    isActive: true,
    notes: '',
  });

  const [errors, setErrors] = useState<any>({});

  const categoryOptions = [
    { value: 'labor', label: 'Labor', color: 'primary' },
    { value: 'material', label: 'Material', color: 'secondary' },
    { value: 'equipment', label: 'Equipment', color: 'warning' },
    { value: 'subcontractor', label: 'Subcontractor', color: 'info' },
    { value: 'overhead', label: 'Overhead', color: 'success' },
    { value: 'other', label: 'Other', color: 'default' },
  ];

  const unitOptions = [
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'each', label: 'Each' },
    { value: 'linear_foot', label: 'Linear Foot' },
    { value: 'square_foot', label: 'Square Foot' },
    { value: 'cubic_foot', label: 'Cubic Foot' },
    { value: 'square_yard', label: 'Square Yard' },
    { value: 'cubic_yard', label: 'Cubic Yard' },
    { value: 'ton', label: 'Ton' },
    { value: 'pound', label: 'Pound' },
    { value: 'gallon', label: 'Gallon' },
    { value: 'liter', label: 'Liter' },
    { value: 'meter', label: 'Meter' },
    { value: 'kilometer', label: 'Kilometer' },
    { value: 'piece', label: 'Piece' },
    { value: 'lot', label: 'Lot' },
    { value: 'lump_sum', label: 'Lump Sum' },
    { value: 'other', label: 'Other' },
  ];

  const subcategoryOptions: { [key: string]: string[] } = {
    labor: [
      'Skilled Labor',
      'General Labor',
      'Supervision',
      'Foreman',
      'Carpenter',
      'Electrician',
      'Plumber',
      'Mason',
      'Roofer',
      'Painter',
      'Other Trades'
    ],
    material: [
      'Concrete',
      'Steel',
      'Lumber',
      'Electrical',
      'Plumbing',
      'Roofing',
      'Insulation',
      'Drywall',
      'Paint',
      'Hardware',
      'Other Materials'
    ],
    equipment: [
      'Heavy Equipment',
      'Tools',
      'Vehicles',
      'Safety Equipment',
      'Temporary Facilities',
      'Other Equipment'
    ],
    subcontractor: [
      'Electrical',
      'Plumbing',
      'HVAC',
      'Roofing',
      'Concrete',
      'Excavation',
      'Landscaping',
      'Other Subs'
    ],
    overhead: [
      'Project Management',
      'Administration',
      'Insurance',
      'Permits',
      'Utilities',
      'Other Overhead'
    ],
    other: [
      'Miscellaneous',
      'Contingency',
      'Other'
    ]
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Cost code is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.unitOfMeasure) {
      newErrors.unitOfMeasure = 'Unit of measure is required';
    }

    if (formData.estimatedQuantity <= 0) {
      newErrors.estimatedQuantity = 'Estimated quantity must be greater than 0';
    }

    if (formData.standardRate < 0) {
      newErrors.standardRate = 'Standard rate cannot be negative';
    }

    if (formData.laborRate < 0) {
      newErrors.laborRate = 'Labor rate cannot be negative';
    }

    if (formData.materialRate < 0) {
      newErrors.materialRate = 'Material rate cannot be negative';
    }

    if (formData.equipmentRate < 0) {
      newErrors.equipmentRate = 'Equipment rate cannot be negative';
    }

    if (formData.overhead < 0 || formData.overhead > 100) {
      newErrors.overhead = 'Overhead must be between 0 and 100%';
    }

    if (formData.profit < 0 || formData.profit > 100) {
      newErrors.profit = 'Profit must be between 0 and 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const response = await api.get('/projects');
        const data = response.data;
        setProjects(data?.data || data || []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const handleInputChange = (field: keyof CostCodeFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'number' ? parseFloat(event.target.value) || 0 : event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof CostCodeFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    
    // Reset subcategory when category changes
    if (field === 'category') {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value,
        subcategory: '',
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You need to log in to create cost codes. Redirecting to login...');
        setLoading(false);
        navigate('/login');
        return;
      }

      const response = await api.post('/cost-codes', {
        project: formData.projectId,
        code: formData.code,
        name: formData.name,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        unitOfMeasure: formData.unitOfMeasure,
        rates: {
          standard: formData.standardRate,
        },
        budget: {
          estimatedQuantity: formData.estimatedQuantity,
          estimatedRate: formData.standardRate,
        },
        notes: formData.notes,
      });

      const payload = response.data;
      const created = payload?.data ?? payload;
      const newId = created?._id ?? created?.id;
      setSuccess(true);
      
      // Redirect to cost code detail page after a short delay
      setTimeout(() => {
        if (newId) {
          navigate(`/cost-codes/${newId}`);
        } else {
          navigate('/cost-codes');
        }
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message || (err instanceof Error ? err.message : 'An error occurred');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/cost-codes');
  };

  const generateCostCode = () => {
    const categoryPrefix = formData.category.substring(0, 2).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);
    const generatedCode = `${categoryPrefix}-${timestamp}`;
    setFormData({
      ...formData,
      code: generatedCode,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create Cost Code
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Cost code created successfully! Redirecting to cost code details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalance color="primary" />
                Basic Information
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 240 }} error={!!errors.projectId}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={formData.projectId}
                    label="Project *"
                    onChange={handleSelectChange('projectId')}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.projectId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.projectId}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Cost Code"
                  value={formData.code}
                  onChange={handleInputChange('code')}
                  error={!!errors.code}
                  helperText={errors.code}
                  sx={{ minWidth: 200 }}
                  required
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={generateCostCode}
                        sx={{ ml: 1 }}
                      >
                        Generate
                      </Button>
                    ),
                  }}
                />

                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ minWidth: 300, flex: 1 }}
                  required
                />
              </Box>

              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Describe what this cost code covers..."
                required
              />

              {/* Category Information */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                <Category color="primary" />
                Category & Classification
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={handleSelectChange('category')}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={option.label}
                            color={option.color as any}
                            size="small"
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Subcategory</InputLabel>
                  <Select
                    value={formData.subcategory}
                    label="Subcategory"
                    onChange={handleSelectChange('subcategory')}
                    disabled={!formData.category}
                  >
                    {(subcategoryOptions[formData.category] || []).map((subcategory) => (
                      <MenuItem key={subcategory} value={subcategory}>
                        {subcategory}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Unit of Measure</InputLabel>
                  <Select
                    value={formData.unitOfMeasure}
                    label="Unit of Measure"
                    onChange={handleSelectChange('unitOfMeasure')}
                  >
                    {unitOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Rate Information */}
              <Typography variant="h6" sx={{ mt: 3 }}>Rate Information</Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Standard Rate"
                  type="number"
                  value={formData.standardRate}
                  onChange={handleInputChange('standardRate')}
                  error={!!errors.standardRate}
                  helperText={errors.standardRate}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />

                <TextField
                  label="Estimated Quantity"
                  type="number"
                  value={formData.estimatedQuantity}
                  onChange={handleInputChange('estimatedQuantity')}
                  error={!!errors.estimatedQuantity}
                  helperText={errors.estimatedQuantity}
                  sx={{ minWidth: 180 }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />

                <TextField
                  label="Labor Rate"
                  type="number"
                  value={formData.laborRate}
                  onChange={handleInputChange('laborRate')}
                  error={!!errors.laborRate}
                  helperText={errors.laborRate}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />

                <TextField
                  label="Material Rate"
                  type="number"
                  value={formData.materialRate}
                  onChange={handleInputChange('materialRate')}
                  error={!!errors.materialRate}
                  helperText={errors.materialRate}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />

                <TextField
                  label="Equipment Rate"
                  type="number"
                  value={formData.equipmentRate}
                  onChange={handleInputChange('equipmentRate')}
                  error={!!errors.equipmentRate}
                  helperText={errors.equipmentRate}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    startAdornment: '$',
                  }}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Overhead %"
                  type="number"
                  value={formData.overhead}
                  onChange={handleInputChange('overhead')}
                  error={!!errors.overhead}
                  helperText={errors.overhead}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    endAdornment: '%',
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 0.1,
                  }}
                />

                <TextField
                  label="Profit %"
                  type="number"
                  value={formData.profit}
                  onChange={handleInputChange('profit')}
                  error={!!errors.profit}
                  helperText={errors.profit}
                  sx={{ minWidth: 150 }}
                  InputProps={{
                    endAdornment: '%',
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 0.1,
                  }}
                />
              </Box>

              {/* Additional Information */}
              <Typography variant="h6" sx={{ mt: 3 }}>Additional Information</Typography>
              
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
                placeholder="Additional notes or comments about this cost code..."
              />

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Cost Code'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CostCodeCreate;